import {
  AccountDetails,
  FormVariant,
  KycAddress,
  KycData,
  KycReviewCurrent,
  KycTier,
  RiskQuestionnaire,
  UserData,
} from "@/types/kycReview";
import { hasOnFileImage } from "../logic/attachments";
import { hasValue } from "../logic/paths";
import { computeRiskProfile } from "../logic/riskProfiler";
import { LoadedSnapshot, MarriageNameMatchesId } from "./types";

// An address is "empty" when none of its locality fields are filled. Country is
// ignored because it commonly defaults to Philippines even when the rest of the
// address was never captured.
function isAddressEmpty(address?: KycAddress): boolean {
  if (!address) return true;
  return (
    !hasValue(address.street) &&
    !hasValue(address.barangay) &&
    !hasValue(address.city) &&
    !hasValue(address.province) &&
    !hasValue(address.zip)
  );
}

const FORM_VARIANTS: FormVariant[] = ["tier_2_refresh", "tier_3_refresh"];

export function isLoadableSnapshot(
  data: KycReviewCurrent | null
): data is KycReviewCurrent & { tier: KycTier; formVariant: FormVariant } {
  return Boolean(
    data &&
      (data.tier === 2 || data.tier === 3) &&
      FORM_VARIANTS.includes(data.formVariant as FormVariant) &&
      data.current &&
      typeof data.current === "object"
  );
}

// Maps the GET /v2/kyc-review/current envelope into the wizard's initial
// editable state. Pure and defensive: missing groups become empty objects.
export function fromSnapshot(data: KycReviewCurrent | null): LoadedSnapshot {
  const current = data?.current || {};
  const tierNum = data?.tier;
  const tier: KycTier | null = tierNum === 2 || tierNum === 3 ? tierNum : null;

  const originalKyc = (current.kyc as KycData) || {};
  const user = (current.user as UserData) || {};
  const riskQuestionnaire = (current.riskQuestionnaire as RiskQuestionnaire) || {};
  const accountDetails = (current.accountDetails as AccountDetails) || {};

  // Default an empty permanent address to the current address so verified
  // clients don't re-enter it; they can still edit before submitting.
  // `originalKyc` keeps the true stored value so change detection stays honest.
  // The "same as current" checkbox starts on when permanent was never captured.
  const currentAddress = originalKyc.currentAddress;
  const sameAsPresentAddress = isAddressEmpty(originalKyc.permanentAddress);
  const kyc: KycData =
    sameAsPresentAddress && !isAddressEmpty(currentAddress)
      ? { ...originalKyc, permanentAddress: { ...(currentAddress as KycAddress) } }
      : originalKyc;

  const marriageNameMatchesId: MarriageNameMatchesId =
    String(originalKyc.civil?.civilStatus || "").trim().toLowerCase() !== "married"
      ? ""
      : hasOnFileImage(originalKyc, "marriageCert")
      ? "no"
      : "yes";

  // Prefer the stored profile label; fall back to the computed answers so a
  // complete prefilled questionnaire still shows a result.
  const riskResult =
    (typeof user.riskProfile === "string" && user.riskProfile) ||
    computeRiskProfile(riskQuestionnaire) ||
    "";

  return {
    tier,
    formVariant: data?.formVariant || "",
    reviewStatus: data?.review?.status || "none",
    draft: { user, kyc, riskQuestionnaire, accountDetails },
    originalKyc,
    riskResult,
    sameAsPresentAddress,
    marriageNameMatchesId,
  };
}
